/**
 * Dernière tâche résolue au fil de la conversation vocale
 */
export interface SessionLastTask {
  id: string;
  number?: number;
  title: string;
}

/**
 * Dernier projet résolu au fil de la conversation vocale
 */
export interface SessionLastProject {
  id: string;
  name: string;
  identifier?: string;
}

/**
 * Entité de contexte conversationnel posée via `session.setContext`
 * (MOKA-207 Phase 4, Lot 3) — personne/sprint(groupe)/release cités par
 * l'utilisateur. `id` est absent quand la résolution front (cf.
 * `SessionVoiceHandler`) n'a pas pu retrouver l'entité correspondante dans le
 * projet en contexte (aucun projet connu, ou aucune correspondance) : `name`
 * reste alors le libellé brut prononcé/tapé — mieux qu'un échec, un projet
 * peut être précisé juste après (cf. `VoiceGuidedFlowService`).
 */
export interface SessionContextEntity {
  id?: string;
  name: string;
}

/**
 * Entités mémorisées par la session vocale (contexte conversationnel court terme).
 * `person`/`group`/`release`/`dueDate` (Lot 3) sont posées par
 * `session.setContext` (résolution dans `SessionVoiceHandler`) et consommées
 * comme défauts NON ÉCRASANTS par les créations guidées
 * (`VoiceGuidedFlowService`) — jamais par une commande GPT directe, qui gère
 * son propre contexte via le payload `session` classique. `dueDate` reste une
 * chaîne (`YYYY-MM-DD`), pas un `SessionContextEntity` : il n'y a pas
 * d'entité métier sous-jacente à résoudre, contrairement à personne/groupe/
 * release.
 */
export interface SessionEntities {
  lastTask?: SessionLastTask;
  lastProject?: SessionLastProject;
  person?: SessionContextEntity;
  group?: SessionContextEntity;
  release?: SessionContextEntity;
  dueDate?: string;
}

/**
 * Question de suivi en attente proposée par le dernier intent exécuté
 * (ex: "Voulez-vous l'assigner à quelqu'un ?")
 */
export interface PendingFollowUp {
  question: string;
  endpoint: string;
  params: Record<string, unknown>;
}

/**
 * Entrée d'historique condensée, telle qu'envoyée à l'Edge Function voice-command
 */
export interface SessionHistoryEntry {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * Payload `session` du contrat de l'Edge Function voice-command (MOKA-204)
 */
export interface SessionPayload {
  entities?: SessionEntities;
  pendingFollowUp?: PendingFollowUp;
  history?: SessionHistoryEntry[];
}

/**
 * Option de réponse rapide affichée sous une bulle assistant (MOKA-206 Phase 3) —
 * boutons des flows guidés et de la clarification de projet. `id` sert à la fois
 * d'étiquette technique (valeur transmise lors de la résolution de l'étape) et de
 * clé de tracking `@for` ; `label` est le texte affiché ET le contenu du message
 * `user` synthétique généré au clic (cf. `VoiceAssistantOverlayComponent`).
 */
export interface VoiceMessageOption {
  id: string;
  label: string;
  /**
   * Choix à privilégier (ex. dernier calendrier utilisé) : rendu en bouton
   * plein, premier de la liste. Purement visuel : la sélection reste la même.
   */
  emphasis?: boolean;
  /** Mention courte affichée dans le bouton (« dernier utilisé »). */
  hint?: string;
}

/**
 * Lien de navigation affiché sous une réponse (MOKA-208 P2 #1).
 *
 * Distinct de `VoiceMessageOption` : une option REPOND à l'assistant (elle
 * devient un message utilisateur et fait avancer un flow), un lien QUITTE la
 * conversation pour aller sur la page concernée. Les confondre ferait
 * apparaître « #12 Ma tâche » comme une réponse de l'utilisateur dans le fil.
 */
export interface VoiceMessageLink {
  label: string;
  route: string;
}

/**
 * Carte de confirmation affichée avant d'exécuter une action marquée
 * `requiresConfirmation` (MOKA-299). Le fil la rend avec deux boutons
 * « Confirmer » / « Annuler » tant que `id` est la confirmation en attente.
 */
export interface VoiceConfirmationCard {
  /** Identifiant de la confirmation en attente (préfixe des ids de boutons). */
  id: string;
  /** Question posée, valeurs comprises (« Supprimer la tâche « X » ? »). */
  title: string;
  /** Détails complémentaires (projet, dates…), une ligne chacun. */
  details: string[];
  confirmOption: VoiceMessageOption;
  cancelOption: VoiceMessageOption;
}

/**
 * Message du fil de conversation affiché dans l'overlay vocal
 */
export interface VoiceChatMessage {
  role: 'user' | 'assistant';
  content: string;
  inputMode: 'voice' | 'text';
  timestamp: number;
  /**
   * Boutons de réponse rapide (flows guidés MOKA-206 Phase 3). VOLATILE : ce
   * champ n'existe que dans le signal `#messages` de `VoiceSessionService`,
   * il n'est jamais transmis à `VoiceSessionStoreService` (la table
   * `voice_messages` n'a pas de colonne pour ça — hors scope de cet agent,
   * pas de migration de schéma). Conséquence assumée : un message avec
   * `options` redevient un message texte simple après `loadSession()` (reprise
   * d'historique) ou un refresh de page — compromis Phase 3, pas un oubli.
   */
  options?: VoiceMessageOption[];
  /**
   * Liens vers les entités citées dans la réponse (tâches listées…). Volatile
   * comme `options`, pour la même raison : pas de colonne en base.
   */
  links?: VoiceMessageLink[];
  /** Carte « Confirmer / Annuler » (volatile, comme `options`). */
  confirmation?: VoiceConfirmationCard;
}

/**
 * Ligne brute de la table `voice_sessions` (colonnes telles que retournées par PostgREST — MOKA-205)
 */
export interface VoiceSessionRow {
  id: string;
  profile_id: string;
  organisation_id: number | null;
  title: string | null;
  entities: SessionEntities;
  pending_follow_up: PendingFollowUp | null;
  created_at: string;
  updated_at: string;
}

/**
 * Ligne brute de la table `voice_messages` (colonnes telles que retournées par PostgREST — MOKA-205)
 */
export interface VoiceMessageRow {
  id: string;
  session_id: string;
  role: 'user' | 'assistant';
  content: string;
  input_mode: 'voice' | 'text' | null;
  created_at: string;
}

/**
 * Forme légère d'une session, utilisée pour la liste d'historique (sidebar/menu)
 */
export interface VoiceSessionSummary {
  id: string;
  title: string | null;
  updatedAt: string;
  createdAt: string;
}

/**
 * Convertit une ligne `voice_messages` en message du fil de conversation.
 * `created_at` (string ISO) est converti en `timestamp` (epoch ms) car
 * `VoiceChatMessage.timestamp` est un `number`. `input_mode` peut être `null`
 * en base (ex: message généré côté serveur) ; on retombe alors sur `'text'`.
 */
export function mapVoiceMessageRowToChatMessage(row: VoiceMessageRow): VoiceChatMessage {
  return {
    role: row.role,
    content: row.content,
    inputMode: row.input_mode ?? 'text',
    timestamp: new Date(row.created_at).getTime()
  };
}

/**
 * Convertit un tableau de lignes `voice_messages` (déjà triées par `created_at asc`)
 * en fil de conversation `VoiceChatMessage[]`
 */
export function mapVoiceMessageRowsToChatMessages(rows: VoiceMessageRow[]): VoiceChatMessage[] {
  return rows.map(mapVoiceMessageRowToChatMessage);
}

/**
 * Extrait les entités mémorisées d'une ligne `voice_sessions` (fallback `{}` si absent)
 */
export function mapVoiceSessionRowToEntities(row: VoiceSessionRow): SessionEntities {
  return row.entities ?? {};
}

/**
 * Extrait la question de suivi en attente d'une ligne `voice_sessions`
 */
export function mapVoiceSessionRowToPendingFollowUp(row: VoiceSessionRow): PendingFollowUp | null {
  return row.pending_follow_up ?? null;
}

/**
 * Convertit une ligne `voice_sessions` en forme légère pour la liste d'historique
 */
export function mapVoiceSessionRowToSummary(row: VoiceSessionRow): VoiceSessionSummary {
  return {
    id: row.id,
    title: row.title,
    updatedAt: row.updated_at,
    createdAt: row.created_at
  };
}
